import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { ArrowBack, EmojiEvents } from '@mui/icons-material';
import { sampleCareers } from '../data/sampleCareers';

interface Career {
  id: string;
  name: string;
  category: string;
  description: string;
  averageSalary: string;
  requiredEducation: string;
  athleteFriendly: boolean;
}

const CareersPage: React.FC = () => {
  const navigate = useNavigate();
  const [careers] = useState<Career[]>(sampleCareers);
  const [loading] = useState(false);
  const [error] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('すべて');

  const categories = useMemo(() => {
    const cats = Array.from(new Set(careers.map(c => c.category)));
    return ['すべて', ...cats];
  }, [careers]);

  const filteredCareers = useMemo(() => {
    if (selectedCategory === 'すべて') return careers;
    return careers.filter(c => c.category === selectedCategory);
  }, [careers, selectedCategory]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
          ホームに戻る
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* ヘッダー */}
      <Box
        sx={{
          bgcolor: '#0066cc',
          color: 'white',
          py: 3,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              sx={{
                color: 'white',
                border: '1px solid rgba(255,255,255,0.5)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  border: '1px solid white',
                },
              }}
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
            >
              ホームに戻る
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              職業一覧（全{careers.length}職種）
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* カテゴリフィルター */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#666', fontWeight: 600 }}>
            カテゴリで絞り込み
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat === 'すべて' ? `すべて (${careers.length})` : `${cat} (${careers.filter(c => c.category === cat).length})`}
                onClick={() => setSelectedCategory(cat)}
                variant={selectedCategory === cat ? 'filled' : 'outlined'}
                color={selectedCategory === cat ? 'primary' : 'default'}
                sx={{
                  fontWeight: selectedCategory === cat ? 600 : 400,
                  cursor: 'pointer',
                }}
              />
            ))}
          </Box>
        </Box>

        {/* 表示件数 */}
        <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
          {filteredCareers.length}件表示中
        </Typography>

        <Grid container spacing={3}>
          {filteredCareers.map((career) => (
            <Grid item xs={12} sm={6} md={4} key={career.id}>
              <Card
                sx={{
                  height: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid #e0e0e0',
                  boxShadow: 'none',
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="h6" component="h2" sx={{ flexGrow: 1, fontSize: '1.05rem', fontWeight: 600, color: '#333' }}>
                      {career.name}
                    </Typography>
                    {career.athleteFriendly && (
                      <EmojiEvents sx={{ color: '#ff9800', fontSize: 24, flexShrink: 0, ml: 1 }} />
                    )}
                  </Box>

                  <Chip
                    label={career.category}
                    size="small"
                    sx={{
                      mb: 2,
                      alignSelf: 'flex-start',
                      bgcolor: '#e3f2fd',
                      color: '#0066cc',
                      fontWeight: 500,
                      border: '1px solid #bbdefb',
                    }}
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      flexGrow: 1,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.7,
                      color: '#666',
                    }}
                  >
                    {career.description}
                  </Typography>

                  <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" display="block" gutterBottom sx={{ fontSize: '0.8rem', color: '#666' }}>
                      平均年収: {career.averageSalary}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.8rem', color: '#666' }}>
                      必要学歴: {career.requiredEducation}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            mt: 6,
            textAlign: 'center',
            bgcolor: 'white',
            borderRadius: 2,
            p: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <EmojiEvents sx={{ color: '#ff9800', fontSize: 20 }} />
            <Typography variant="body1" sx={{ color: '#666' }}>
              マークは「アスリート経験が活かせる職業」です
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: '#333', mb: 3, mt: 2 }}>
            あなたに合った職業を診断してみませんか？
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: '#0066cc',
              color: 'white',
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
              '&:hover': {
                bgcolor: '#0052a3',
                boxShadow: '0 6px 16px rgba(25,118,210,0.4)',
              },
            }}
            onClick={() => navigate('/assessment')}
          >
            診断を受けて最適な職業を見つける
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CareersPage;
